/**
 * Module de suivi analytique en temps réel pour Zeyilo
 * Capture les visites, la durée de présence active, le défilement (scroll depth),
 * les sections atteintes, la section de sortie (drop-off), les clics et le contexte technique.
 */

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const VISITOR_ID_KEY = 'zy_visitor_id';
const SESSION_ID_KEY = 'zy_session_id';

export function getOrCreateVisitorId(): string {
  try {
    let vid = localStorage.getItem(VISITOR_ID_KEY);
    if (!vid) {
      vid = 'v_' + generateUUID();
      localStorage.setItem(VISITOR_ID_KEY, vid);
    }
    return vid;
  } catch {
    return 'v_temp_' + generateUUID();
  }
}

export function getOrCreateSessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sid) {
      sid = 's_' + generateUUID();
      sessionStorage.setItem(SESSION_ID_KEY, sid);
    }
    return sid;
  } catch {
    return 's_temp_' + generateUUID();
  }
}

interface SectionTimeMap {
  [sectionId: string]: number; // temps cumulé en secondes
}

class SiteAnalyticsTracker {
  private visitorId = '';
  private sessionId = '';
  private isInitialized = false;

  private startTime = 0;
  private activeSeconds = 0;
  private lastActiveTimestamp = 0;
  private isUserActive = true;
  private clicksCount = 0;

  private maxScrollDepth = 0;
  private currentSection = 'hero';
  private maxSectionReached = 'hero';
  private sectionTimes: SectionTimeMap = {};
  private activeSectionStart = 0;

  private heartbeatInterval: any = null;
  private activeTimerInterval: any = null;
  private observer: IntersectionObserver | null = null;

  public init() {
    if (typeof window === 'undefined' || this.isInitialized) return;

    // Détection stricte des robots, crawlers et navigateurs automatisés (Lighthouse, Googlebot, Puppeteer)
    if (typeof navigator !== 'undefined') {
      if (navigator.webdriver) return; // Automatisé (Selenium, Puppeteer, Lighthouse, headless)
      const ua = navigator.userAgent || '';
      if (/bot|googlebot|crawler|spider|robot|crawling|lighthouse|headlesschrome|google-inspectiontool|bingbot|yandex|duckduckbot|baiduspider|semrush|ahrefs|petalbot|bytespider/i.test(ua)) {
        return;
      }
    }

    this.isInitialized = true;

    this.visitorId = getOrCreateVisitorId();
    this.sessionId = getOrCreateSessionId();
    this.startTime = Date.now();
    this.lastActiveTimestamp = Date.now();
    this.activeSectionStart = Date.now();

    // 1. Détection de l'activité utilisateur (souris, toucher, clavier, scroll)
    const onUserActivity = () => {
      this.lastActiveTimestamp = Date.now();
      this.isUserActive = true;
    };

    window.addEventListener('mousemove', onUserActivity, { passive: true });
    window.addEventListener('touchstart', onUserActivity, { passive: true });
    window.addEventListener('keydown', onUserActivity, { passive: true });
    window.addEventListener('scroll', onUserActivity, { passive: true });

    // 2. Compteur de secondes actives
    this.activeTimerInterval = setInterval(() => {
      const now = Date.now();
      // Si la fenêtre est visible et l'utilisateur a bougé dans les 45 dernières secondes
      if (document.visibilityState === 'visible' && (now - this.lastActiveTimestamp < 45000)) {
        this.activeSeconds += 1;
        
        // Incrémente aussi le temps passé sur la section courante
        if (this.currentSection) {
          this.sectionTimes[this.currentSection] = (this.sectionTimes[this.currentSection] || 0) + 1;
        }
      }
    }, 1000);

    // 3. Suivi du scroll depth
    const onScroll = () => {
      const docHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight,
        window.innerHeight
      );
      const scrollPos = window.scrollY + window.innerHeight;
      const currentPercent = Math.min(100, Math.round((scrollPos / docHeight) * 100));

      if (currentPercent > this.maxScrollDepth) {
        this.maxScrollDepth = currentPercent;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // 4. Observation des sections de la page
    this.setupSectionObserver();

    // 5. Clics sur boutons et CTA
    document.addEventListener('click', this.handleDocumentClick, { capture: true, passive: true });

    // 6. Envoi immédiat du premier événement de visite
    this.sendPayload(false);

    // 7. Battement de cœur périodique (toutes les 15 secondes)
    this.heartbeatInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.sendPayload(false);
      }
    }, 15000);

    // 8. Capture de la sortie de la page (fermeture d'onglet, navigation, passage en arrière-plan)
    const handleExit = () => {
      this.sendPayload(true);
    };

    window.addEventListener('pagehide', handleExit);
    window.addEventListener('beforeunload', handleExit);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        handleExit();
      }
    });
  }

  /**
   * Observe les éléments avec identifiant pour mesurer le niveau de section franchi
   */
  public setupSectionObserver() {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;

    if (this.observer) {
      this.observer.disconnect();
    }

    const sectionElements = document.querySelectorAll<HTMLElement>(
      'section[id], footer[id], [data-section], #features, #profils, #how, #temoignages, #cta-banner, #faq, #footer'
    );

    if (sectionElements.length === 0) {
      // Retente après que le DOM soit complètement rendu
      setTimeout(() => this.setupSectionObserver(), 1200);
      return;
    }

    const SECTIONS_RANK: Record<string, number> = {
      hero: 1,
      features: 2,
      profils: 3,
      how: 4,
      temoignages: 5,
      'cta-banner': 6,
      cta: 6,
      faq: 7,
      footer: 8
    };

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
            const secId = entry.target.id || entry.target.getAttribute('data-section') || 'inconnu';
            this.currentSection = secId;

            const currentRank = SECTIONS_RANK[secId] || 0;
            const maxRank = SECTIONS_RANK[this.maxSectionReached] || 0;
            if (currentRank > maxRank) {
              this.maxSectionReached = secId;
            }

            if (!this.sectionTimes[secId]) {
              this.sectionTimes[secId] = 0;
            }
          }
        });
      },
      { threshold: [0.2, 0.5] }
    );

    sectionElements.forEach((el) => this.observer?.observe(el));
  }

  /**
   * Clics automatiques sur les CTA importants
   */
  private handleDocumentClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const clickable = target.closest<HTMLElement>('button, a, [data-analytics-click]');
    if (!clickable) return;

    this.clicksCount += 1;

    const text = (clickable.innerText || clickable.getAttribute('aria-label') || clickable.title || '').trim().slice(0, 80);
    const href = clickable.getAttribute('href') || undefined;
    const clickType = clickable.getAttribute('data-analytics-click') || (href ? 'link_click' : 'button_click');

    this.trackEvent({
      eventType: 'cta_click',
      eventName: clickType,
      targetText: text || href || 'Bouton sans texte',
      metadata: {
        href,
        currentSection: this.currentSection,
        scrollDepth: this.maxScrollDepth
      }
    });
  };

  /**
   * Envoi d'un événement ponctuel à l'API
   */
  public trackEvent(event: {
    eventType: string;
    eventName?: string;
    targetText?: string;
    metadata?: Record<string, any>;
  }) {
    if (!this.sessionId) {
      this.sessionId = getOrCreateSessionId();
      this.visitorId = getOrCreateVisitorId();
    }

    const payload = {
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      ...event
    };

    const url = '/api/analytics/event';
    const bodyStr = JSON.stringify(payload);

    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: bodyStr,
        keepalive: true
      }).catch(() => {});
    } catch {
      // Ignorer les erreurs d'envoi réseau
    }
  }

  /**
   * Envoi de l'état de session au serveur (visite, temps actif, scroll, exit section)
   */
  public sendPayload(isExit = false) {
    if (!this.sessionId || !this.visitorId) return;

    const now = Date.now();
    const durationSeconds = Math.max(1, Math.round((now - this.startTime) / 1000));

    // Construction de la liste des sections vues avec temps
    const sectionsViewed = Object.entries(this.sectionTimes).map(([sec, secs]) => ({
      section: sec,
      seconds: secs
    }));

    // Analyse des paramètres UTM
    let utmSource: string | undefined;
    let utmMedium: string | undefined;
    let utmCampaign: string | undefined;
    let utmTerm: string | undefined;
    let utmContent: string | undefined;

    try {
      const sp = new URLSearchParams(window.location.search);
      utmSource = sp.get('utm_source') || sp.get('ref') || undefined;
      utmMedium = sp.get('utm_medium') || undefined;
      utmCampaign = sp.get('utm_campaign') || undefined;
      utmTerm = sp.get('utm_term') || undefined;
      utmContent = sp.get('utm_content') || undefined;
    } catch {}

    const payload = {
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      pageUrl: window.location.href,
      pagePath: window.location.pathname,
      pageTitle: document.title,
      referrer: document.referrer || '',
      utmSource,
      utmMedium,
      utmCampaign,
      utmTerm,
      utmContent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language || '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      durationSeconds,
      activeSeconds: this.activeSeconds,
      scrollDepthPercent: this.maxScrollDepth,
      maxSectionReached: this.maxSectionReached,
      exitSection: this.currentSection || this.maxSectionReached,
      sectionsViewed,
      clicksCount: this.clicksCount,
      isExit
    };

    const payloadStr = JSON.stringify(payload);
    const url = '/api/analytics/track';

    if (isExit && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      // L'envoi via sendBeacon avec text/plain garantit la transmission immédiate sans préflight CORS
      const blob = new Blob([payloadStr], { type: 'text/plain;charset=UTF-8' });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) return;
    }

    // Fallback standard fetch avec keepalive
    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payloadStr,
        keepalive: true
      }).catch(() => {});
    } catch {
      // Silencieux
    }
  }

  public destroy() {
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    if (this.activeTimerInterval) clearInterval(this.activeTimerInterval);
    if (this.observer) this.observer.disconnect();
    document.removeEventListener('click', this.handleDocumentClick, { capture: true });
    this.isInitialized = false;
  }
}

export const siteAnalytics = new SiteAnalyticsTracker();
